/**
 * "Previous / next violation" — which station interval the walk moves to.
 *
 * Pure, so the rule is pinned by unit tests rather than by a dock test that
 * has to click its way to every edge. The dock's two chevron buttons and the
 * `Shift+[` / `Shift+]` hotkeys both land here, so the button and the key can
 * never disagree about where "next" is.
 *
 * The rule:
 *  - Relative to the interval the walk STANDS in (`binAtStation`, last bin's
 *    end inclusive — the same rule the cross-section uses), never to the
 *    walk's exact station: a walk parked mid-way through a violating interval
 *    must move to the NEXT violating one, not "snap back" to the start of its
 *    own.
 *  - A station outside every interval (before the corridor, past it, or in a
 *    gap a filtered run left) is compared by station instead, so "next" is
 *    still the first violating interval AHEAD of where the reader is.
 *  - No walk at all: "next" is the first violating interval, "previous" has
 *    nowhere to go — the reader has not stood anywhere yet.
 *  - No wrap. Wrapping from the last violation back to the first reads, in a
 *    corridor of hundreds of intervals, as the walk jumping to a random place.
 */
import type { ClearanceBin } from './types';
import { binAtStation } from './table';

export type ViolationStepDir = -1 | 1;

/**
 * The violating interval before (`dir = -1`) or after (`dir = 1`) the one the
 * INTERNAL station `station` stands in, or null when there is none that way.
 * Bins are taken in array order, which is station order (the worker's
 * contract, shared with the section grid).
 */
export function adjacentViolationBin(
  bins: readonly ClearanceBin[],
  station: number | null,
  dir: ViolationStepDir,
): ClearanceBin | null {
  if (station == null) return dir === 1 ? (bins.find((b) => b.violationCount > 0) ?? null) : null;
  const at = binAtStation(bins, station);
  const pos = at == null ? -1 : bins.findIndex((b) => b.i === at);
  if (dir === 1) {
    for (let k = 0; k < bins.length; k++) {
      const b = bins[k];
      if (b.violationCount <= 0) continue;
      if (pos >= 0 ? k > pos : b.staStartInternalM >= station) return b;
    }
    return null;
  }
  for (let k = bins.length - 1; k >= 0; k--) {
    const b = bins[k];
    if (b.violationCount <= 0) continue;
    if (pos >= 0 ? k < pos : b.staEndInternalM <= station) return b;
  }
  return null;
}
